import type { CardProps } from '@/scopes/prices/components/TransportSelector/Card'
import { airlineCodesMap, AirlineIcon } from '@sunwebgroup/uds-ui/airline-icon'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { identity } from 'ramda'
import { useIntl } from '@/intl'
import { LuggageType } from '@/scopes/prices/domain/transports/types'
import { UndefinedTime } from '@/scopes/prices/infrastructure/transport/flight/flightSelectorResponseMapper'
import styles from './FlightCardNew.module.css'

export function FlightCardNew({
  id,
  arrivalDate,
  departureDate,
  route,
  airline,
  luggage,
  flightType,
  averagePrice,
  isSelected,
  remark,
  setSelected = identity,
  noActiveState,
  flightDuration,
  stopovers,
}: CardProps) {
  const intl = useIntl()

  function formatFlightDurationToHours(minutes?: number | null): string {
    if (minutes == null)
      return ''

    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60

    const hoursStr = intl.formatNumber(hours, { style: 'unit', unit: 'hour', unitDisplay: 'narrow' })
    const minsStr = intl.formatNumber(mins, { style: 'unit', unit: 'minute', unitDisplay: 'narrow', minimumIntegerDigits: 2 })

    if (hours === 0) {
      return minsStr
    }

    if (mins === 0) {
      return hoursStr
    }

    return `${hoursStr} ${minsStr}`
  }

  function formatDate(date: Date, intl: ReturnType<typeof useIntl>) {
    const formatted = intl.formatDate(date, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    return formatted.charAt(0).toUpperCase() + formatted.slice(1)
  }

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'
  const isClickable = !isSelected && !noActiveState
  const selectCard = () => isClickable && setSelected(id)

  return (
    <div
      className={cn(styles.listItemRoot, isSelected && !noActiveState && styles.listItemRootSelected)}
      onClick={selectCard}
      role="presentation"
    >
      <div className={styles.flightContent}>
        <div className={styles.flightContentMain}>
          <div className={styles.flightContentHeading}>
            <div className={styles.flightDepartureDate}>
              <Icon glyph="AirplaneTakingOff" size={16} />
              <Typography variant="body-s-regular-tight">
                {formatDate(new Date(departureDate.rawDate), intl)}
              </Typography>
            </div>
            <div className={styles.flightArrivalDate}>
              <Typography variant="body-s-regular-tight">
                {formatDate(new Date(arrivalDate.rawDate), intl)}
              </Typography>
              <Icon glyph="AirplaneReturn" size={16} />
            </div>
          </div>
          <div className={styles.flightDetails}>
            <div className={styles.flightDuration}>
              {departureDate.time === UndefinedTime
                ? (<Typography className={styles.noFlightDurationText} variant="display-g-regular">{departureDate.time}</Typography>)
                : (<Typography variant="display-g-regular">{departureDate.time}</Typography>)}
              <div className={styles.flightDurationWrapper}>
                <div className={styles.track} />
                <div className={styles.containerBetweenTracks}>
                  {Boolean(flightDuration) && <Typography className={styles.flightDurationValue} variant="body-s-regular-flat">{formatFlightDurationToHours(flightDuration)}</Typography>}
                  <Typography className={styles.textBetweenTracks} variant="body-xs-regular-flat">
                    {stopovers && stopovers.length ? stopovers.join(', ') : intl.formatMessage({ id: 'prices_transport_selector_direct' })}
                  </Typography>
                </div>
                <div className={styles.track} />
              </div>
              {arrivalDate.time === UndefinedTime
                ? (<Typography className={styles.noFlightDurationText} variant="display-g-regular">{arrivalDate.time}</Typography>)
                : (<Typography variant="display-g-regular">{arrivalDate.time}</Typography>)}
            </div>
            <div className={styles.flightOutboundInbound}>
              <Typography variant="body-s-regular-tight">{route.from.name}</Typography>
              <Typography variant="body-s-regular-tight">{route.to.name}</Typography>
            </div>
          </div>
          <div className={styles.featuresWrapper}>
            <div className={styles.infoBlock}>
              {airline && (
                <div className={styles.airlineWrapper}>
                  <AirlineIcon className={styles.airlineIcon} glyph={airlineCodesMap[airline.id]} size="md" />
                  <Typography variant="body-s-regular-tight">{airline.name}</Typography>
                </div>
              )}
              {luggage && flightType && luggage.map(luggageItem => (
                <div className={styles.infoItem} key={luggageItem.type}>
                  <Icon className={styles.icon} glyph={luggageItem.type === LuggageType.Hold ? 'Suitcase' : 'BackpackWithCheckMark'} size={18} />
                  <Typography variant="body-s-regular-tight">
                    {luggageItem.type === LuggageType.Hold && `${luggageItem.weight} kg`}
                    {luggageItem.type === LuggageType.Hand
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </div>
              ))}
              <div className={styles.infoItem}>
                <Icon className={styles.icon} glyph={`${flightType === 'standard' ? 'AirplaneToTop' : 'AirplaneWithArrowsCirclePath'}`} size={18} />
                <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </div>
            </div>
            {remark && <Typography as="div" className={styles.remark}>{remark}</Typography>}
          </div>
        </div>
      </div>
      <div className={cn(styles.priceWrapper, isSelected && !noActiveState && styles.priceWrapperSelected)}>
        <div>
          <Typography variant="body-m-bold-tight">{intl.formatCurrency(averagePrice, { showSign: true })}</Typography>
          <Typography variant="body-m-bold-tight">
            {' '}
            {intl.formatMessage({ id: 'prices_transport_selector_price-per-person_abbreviation' })}
          </Typography>
          {averagePrice === 0 && (
            <Tag className={styles.tag} variant="success-tinted">{intl.formatMessage({ id: 'prices_transport_selector_flight-selector_cheapest' })}</Tag>
          )}
        </div>
        { !isSelected && (
          <Button size="S" variant="secondary">
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-select',
            })}
          </Button>
        )}
        { isSelected && (
          <div className={styles.selectedLabel}>
            <Icon glyph="CheckMarkSmall" />
            <Typography variant="body-m-regular-tight">{intl.formatMessage({ id: 'prices_transport_selector_flight-selector_selected' })}</Typography>
          </div>
        )}
      </div>
    </div>
  )
}
